/**
 * Stage 3 — Snow vertical slice (Campaign V3 / M96).
 *
 * DEV-gated: not registered in production MAIN_CAMPAIGN until content is complete.
 * Import via DEV labs / explicit feature flag only.
 */

import type { ChapterDefinition, StageDefinition, StageWaveGroupSpec, StageWaveSpec } from '../combat/campaign/types'
import type { DifficultyId, EnemyRank } from '../combat/types/primitives'
import type { StageFirstClearRewards } from '../combat/campaign/firstClear'

const ALL_DIFFICULTIES: DifficultyId[] = ['easy', 'normal', 'hard', 'insane']

function rewards(ordinal: number): StageFirstClearRewards {
  const base = 8 + ordinal * 2
  return { easy: base, normal: base * 2, hard: base * 4, insane: base * 8 }
}

function g(
  enemyId: string,
  enemyLevel: number,
  count: number,
  interval: number,
  delay: number,
  enemyRank: EnemyRank = 'NORMAL',
  routeId?: string
): StageWaveGroupSpec {
  return { enemyId, enemyLevel, enemyRank, count, interval, delay, routeId }
}

function w(id: string, completionReward: number, groups: StageWaveGroupSpec[]): StageWaveSpec {
  return { id, groups, completionReward }
}

function stage(
  ordinal: number,
  displayLabel: string,
  mapId: string,
  waves: StageWaveSpec[],
  opts?: { bossLevel?: boolean; allowedTowerIds?: string[] }
): StageDefinition {
  return {
    id: `chapter3-stage${ordinal}`,
    chapterId: 'chapter-3',
    displayLabel,
    mapId,
    waves,
    allowedTowerIds: opts?.allowedTowerIds,
    availableDifficulties: ALL_DIFFICULTIES,
    firstClearRewards: rewards(ordinal),
    bossLevel: opts?.bossLevel
  }
}

const ALL_TOWERS = ['fire', 'frost', 'cannon', 'poison', 'aa', 'mage']

/**
 * Preview / partial Stage 3 — not wired into MAIN_CAMPAIGN.
 * Enable via `STAGE3_SNOW_DEV` registry hook when ready.
 */
export const CHAPTER_3_DEV: ChapterDefinition = {
  id: 'chapter-3',
  campaignId: 'campaign-main',
  displayName: 'Stage 3 — Snow (Preview)',
  chapterNumber: 3,
  biomeId: 'snow',
  enemyFamilyIds: ['yeti'],
  stageIds: [
    'chapter3-stage1',
    'chapter3-stage2',
    'chapter3-stage3',
    'chapter3-stage4',
    'chapter3-stage5'
  ]
}

/** Representative snow slice 3-1…3-5 (boss). Maps reuse production geometry with snow biome theme. */
export const CHAPTER_3_DEV_STAGES: StageDefinition[] = [
  stage(1, '3-1', 'valley-v', [
    w('c3s1-w1', 40, [g('normal', 4, 6, 0.75, 0.4)]),
    w('c3s1-w2', 45, [g('fast', 4, 5, 0.55, 0.3)]),
    w('c3s1-w3', 50, [g('normal', 5, 6, 0.7, 0.3), g('fast', 4, 4, 0.5, 1.0)]),
    w('c3s1-w4', 55, [g('tank', 4, 2, 1.5, 0.4)]),
    w('c3s1-w5', 60, [g('normal', 5, 8, 0.65, 0.3)]),
    w('c3s1-w6', 70, [g('regen', 5, 3, 1.2, 0.4)]),
    w('c3s1-w7', 80, [g('tank', 5, 2, 1.4, 0.3), g('fast', 5, 5, 0.5, 0.8)]),
    w('c3s1-w8', 90, [g('normal', 6, 8, 0.6, 0.3)]),
    w('c3s1-w9', 100, [g('shield', 5, 2, 1.3, 0.4)]),
    w('c3s1-w10', 110, [g('tank', 6, 2, 1.3, 0.3), g('regen', 5, 2, 1.1, 1.0)])
  ], { allowedTowerIds: ALL_TOWERS }),

  stage(2, '3-2', 'crossroads-c', [
    w('c3s2-w1', 45, [g('normal', 5, 7, 0.7, 0.3)]),
    w('c3s2-w2', 50, [g('fast', 5, 6, 0.5, 0.3)]),
    w('c3s2-w3', 55, [g('tank', 5, 2, 1.4, 0.4)]),
    w('c3s2-w4', 60, [g('normal', 6, 6, 0.65, 0.3), g('fast', 5, 4, 0.5, 0.9)]),
    w('c3s2-w5', 70, [g('regen', 6, 3, 1.15, 0.4)]),
    w('c3s2-w6', 80, [g('shield', 6, 2, 1.25, 0.3), g('normal', 6, 5, 0.6, 0.8)]),
    w('c3s2-w7', 90, [g('flyer', 5, 4, 0.7, 0.4, 'NORMAL', 'ground-a')]),
    w('c3s2-w8', 100, [g('tank', 6, 2, 1.3, 0.3), g('flyer', 5, 3, 0.65, 0.9, 'NORMAL', 'ground-a')]),
    w('c3s2-w9', 110, [g('fast', 6, 6, 0.48, 0.3), g('regen', 6, 2, 1.1, 1.0)]),
    w('c3s2-w10', 120, [g('shield', 7, 2, 1.2, 0.3), g('tank', 6, 2, 1.3, 0.8)]),
    w('c3s2-w11', 130, [g('normal', 7, 8, 0.55, 0.3), g('flyer', 6, 4, 0.6, 1.0, 'NORMAL', 'ground-a')]),
    w('c3s2-w12', 140, [g('tank', 7, 3, 1.25, 0.3), g('fast', 7, 5, 0.45, 0.9)])
  ], { allowedTowerIds: ALL_TOWERS }),

  stage(3, '3-3', 'rift-r', [
    w('c3s3-w1', 50, [g('normal', 6, 8, 0.65, 0.3)]),
    w('c3s3-w2', 55, [g('fast', 6, 6, 0.48, 0.3)]),
    w('c3s3-w3', 60, [g('tank', 6, 3, 1.35, 0.4)]),
    w('c3s3-w4', 70, [g('regen', 6, 3, 1.1, 0.3), g('normal', 6, 5, 0.6, 0.9)]),
    w('c3s3-w5', 80, [g('shield', 7, 2, 1.2, 0.3)]),
    w('c3s3-w6', 90, [g('flyer', 6, 5, 0.65, 0.4, 'NORMAL', 'ground-a')]),
    w('c3s3-w7', 100, [g('guardian', 6, 2, 1.4, 0.4)]),
    w('c3s3-w8', 110, [g('crystal', 6, 2, 1.3, 0.4), g('fast', 7, 5, 0.45, 0.9)]),
    w('c3s3-w9', 120, [g('tank', 7, 3, 1.25, 0.3), g('flyer', 6, 4, 0.6, 1.0, 'NORMAL', 'ground-a')]),
    w('c3s3-w10', 130, [g('regen', 7, 3, 1.05, 0.3), g('shield', 7, 2, 1.15, 0.9)]),
    w('c3s3-w11', 140, [g('normal', 8, 8, 0.55, 0.3)]),
    w('c3s3-w12', 150, [g('guardian', 7, 2, 1.35, 0.3), g('crystal', 7, 2, 1.25, 0.8)]),
    w('c3s3-w13', 160, [g('tank', 8, 3, 1.2, 0.3), g('flyer', 7, 5, 0.55, 0.9, 'NORMAL', 'ground-a')]),
    w('c3s3-w14', 180, [g('fast', 8, 8, 0.42, 0.25), g('regen', 8, 3, 1.0, 1.0)])
  ], { allowedTowerIds: ALL_TOWERS }),

  stage(4, '3-4', 'twinspire', [
    w('c3s4-w1', 55, [
      g('normal', 7, 5, 0.7, 0.3, 'NORMAL', 'ground-a'),
      g('normal', 7, 5, 0.7, 0.35, 'NORMAL', 'ground-b')
    ]),
    w('c3s4-w2', 60, [
      g('fast', 7, 5, 0.5, 0.3, 'NORMAL', 'ground-a'),
      g('fast', 7, 4, 0.5, 0.4, 'NORMAL', 'ground-b')
    ]),
    w('c3s4-w3', 70, [g('tank', 7, 2, 1.35, 0.3, 'NORMAL', 'ground-a'), g('tank', 7, 2, 1.35, 0.4, 'NORMAL', 'ground-b')]),
    w('c3s4-w4', 80, [g('flyer', 7, 4, 0.65, 0.3, 'NORMAL', 'ground-a'), g('flyer', 7, 3, 0.65, 0.4, 'NORMAL', 'ground-b')]),
    w('c3s4-w5', 90, [g('regen', 8, 3, 1.1, 0.3, 'NORMAL', 'ground-a'), g('shield', 8, 2, 1.2, 0.4, 'NORMAL', 'ground-b')]),
    w('c3s4-w6', 100, [g('guardian', 7, 2, 1.4, 0.3, 'NORMAL', 'ground-a')]),
    w('c3s4-w7', 110, [g('crystal', 8, 2, 1.25, 0.3, 'NORMAL', 'ground-b'), g('fast', 8, 5, 0.45, 0.8, 'NORMAL', 'ground-a')]),
    w('c3s4-w8', 120, [g('tank', 8, 2, 1.25, 0.3, 'NORMAL', 'ground-a'), g('tank', 8, 2, 1.25, 0.4, 'NORMAL', 'ground-b')]),
    w('c3s4-w9', 130, [g('normal', 8, 6, 0.55, 0.3, 'NORMAL', 'ground-a'), g('flyer', 8, 4, 0.55, 0.5, 'NORMAL', 'ground-b')]),
    w('c3s4-w10', 140, [g('shield', 9, 2, 1.15, 0.3, 'NORMAL', 'ground-a'), g('regen', 9, 2, 1.05, 0.4, 'NORMAL', 'ground-b')]),
    w('c3s4-w11', 150, [g('guardian', 8, 2, 1.3, 0.3, 'NORMAL', 'ground-a'), g('crystal', 8, 2, 1.2, 0.4, 'NORMAL', 'ground-b')]),
    w('c3s4-w12', 160, [g('fast', 9, 6, 0.42, 0.25, 'NORMAL', 'ground-a'), g('fast', 9, 5, 0.42, 0.3, 'NORMAL', 'ground-b')]),
    w('c3s4-w13', 170, [g('tank', 9, 3, 1.2, 0.3, 'NORMAL', 'ground-a'), g('flyer', 8, 5, 0.5, 0.5, 'NORMAL', 'ground-b')]),
    w('c3s4-w14', 180, [g('normal', 9, 7, 0.5, 0.3, 'NORMAL', 'ground-a'), g('normal', 9, 7, 0.5, 0.35, 'NORMAL', 'ground-b')]),
    w('c3s4-w15', 200, [
      g('tank', 9, 2, 1.15, 0.3, 'ELITE', 'ground-a'),
      g('regen', 9, 2, 1.0, 0.4, 'ELITE', 'ground-b'),
      g('flyer', 9, 4, 0.5, 1.0, 'NORMAL', 'ground-a')
    ])
  ], { allowedTowerIds: ALL_TOWERS }),

  stage(5, '3-5', 'confluence', [
    w('c3s5-w1', 60, [g('normal', 8, 8, 0.6, 0.3, 'NORMAL', 'ground-a')]),
    w('c3s5-w2', 65, [g('fast', 8, 7, 0.45, 0.3, 'NORMAL', 'ground-b')]),
    w('c3s5-w3', 70, [g('tank', 8, 3, 1.3, 0.3)]),
    w('c3s5-w4', 80, [g('flyer', 8, 5, 0.6, 0.3, 'NORMAL', 'ground-a')]),
    w('c3s5-w5', 90, [g('regen', 9, 3, 1.05, 0.3), g('shield', 9, 2, 1.15, 0.8)]),
    w('c3s5-w6', 100, [g('guardian', 8, 2, 1.35, 0.3)]),
    w('c3s5-w7', 110, [g('crystal', 9, 3, 1.2, 0.3)]),
    w('c3s5-w8', 120, [g('tank', 9, 3, 1.2, 0.3), g('flyer', 9, 4, 0.55, 0.9, 'NORMAL', 'ground-b')]),
    w('c3s5-w9', 130, [g('fast', 9, 8, 0.4, 0.25)]),
    w('c3s5-w10', 140, [g('normal', 10, 8, 0.5, 0.3), g('regen', 9, 3, 1.0, 1.0)]),
    w('c3s5-w11', 150, [g('shield', 10, 3, 1.1, 0.3), g('guardian', 9, 2, 1.3, 0.9)]),
    w('c3s5-w12', 160, [g('flyer', 10, 6, 0.5, 0.3, 'NORMAL', 'ground-a'), g('crystal', 9, 2, 1.15, 1.0)]),
    w('c3s5-w13', 170, [g('tank', 10, 3, 1.15, 0.3, 'ELITE')]),
    w('c3s5-w14', 180, [g('fast', 10, 6, 0.4, 0.25), g('flyer', 10, 4, 0.5, 0.8, 'NORMAL', 'ground-b')]),
    w('c3s5-w15', 200, [g('guardian', 10, 2, 1.25, 0.3), g('crystal', 10, 2, 1.15, 0.8)]),
    w('c3s5-w16', 220, [g('normal', 10, 10, 0.45, 0.25), g('regen', 10, 3, 0.95, 1.0)]),
    w('c3s5-w17', 240, [g('tank', 11, 3, 1.1, 0.3), g('flyer', 10, 5, 0.48, 0.9, 'ELITE', 'ground-a')]),
    w('c3s5-w18', 280, [
      g('boss', 10, 1, 0, 1.5, 'BOSS'),
      g('flyer', 10, 4, 0.7, 3.0, 'NORMAL', 'ground-a'),
      g('tank', 10, 2, 1.2, 4.0, 'NORMAL', 'ground-b')
    ])
  ], { bossLevel: true, allowedTowerIds: ALL_TOWERS })
]

/** Feature flag — production registry must keep this false until Stage 3 is complete. */
export const STAGE3_SNOW_DEV_ENABLED = false
